import React, {useState, useEffect, useContext} from "react";
import {AuthContext} from "../contexts/AuthContext";

export default function BookingsTable() {
    const {fetchWithAuth} = useContext(AuthContext);
    const [bookings, setBookings] = useState([]);
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [hotelFilter, setHotelFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [hotels, setHotels] = useState([]);
    const statuses = ["pending", "confirmed", "canceled"];

    // Fetch bookings
    const fetchBookings = async (url = "/api/bookings") => {
        setLoading(true);
        setError("");

        try {
            const params = new URLSearchParams();
            if (search) params.append("search", search);
            if (hotelFilter) params.append("hotel", hotelFilter);
            if (statusFilter) params.append("status", statusFilter);

            let finalUrl = url.includes("?")
                ? `${url}&${params.toString()}`
                : `${url}?${params.toString()}`;

            const data = await fetchWithAuth(finalUrl);
            setBookings(data.data || []);
            setLinks(data.links || []);
        } catch (err) {
            setError(err.message || "Failed to fetch bookings.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch distinct hotel names for filter dropdown
    const fetchHotels = async () => {
        try {
            const data = await fetchWithAuth("/api/hotels");
            setHotels(data || []); // hotels is an array of strings
        } catch (err) {
            console.error("Failed to fetch hotels:", err);
        }
    };

    useEffect(() => {
        fetchHotels();
    }, []);

    useEffect(() => {
        fetchBookings();
    }, [search, hotelFilter, statusFilter]);

    return (
        <div className="p-4">
            {/* Search & Filter Bar */}
            <div className="mb-4 flex gap-2 flex-wrap">
                <input
                    type="text"
                    placeholder="Search bookings..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border rounded px-3 py-2 flex-1 min-w-[200px]"
                />
                <select
                    value={hotelFilter}
                    onChange={(e) => setHotelFilter(e.target.value)}
                    className="border rounded px-3 py-2"
                >
                    <option value="">All Hotels</option>
                    {hotels.map((hotel, index) => (
                        <option key={index} value={hotel}>
                            {hotel}
                        </option>
                    ))}
                </select>
            </div>

            {loading ? (
                <p>Loading...</p>
            ) : error ? (
                <p className="text-red-500">{error}</p>
            ) : (
                <>
                    {/* Table */}
                    <table className="w-full border-collapse border">
                        <thead>
                        <tr className="bg-gray-100">
                            <th className="border px-3 py-2">Hotel</th>
                            <th className="border px-3 py-2">Full Name</th>
                            <th className="border px-3 py-2">Email</th>
                            <th className="border px-3 py-2">Phone</th>
                            <th className="border px-3 py-2">Guests</th>
                            <th className="border px-3 py-2">Notes</th>
                            <th className="border px-3 py-2">Start Date</th>
                            <th className="border px-3 py-2">Created At</th>
                            <th className="border px-3 py-2">Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        {bookings.length > 0 ? (
                            bookings.map((booking) => (
                                <tr key={booking.id}>
                                    <td className="border px-3 py-2">{booking.hotel}</td>
                                    <td className="border px-3 py-2">{booking.full_name}</td>
                                    <td className="border px-3 py-2">{booking.email}</td>
                                    <td className="border px-3 py-2">{booking.phone}</td>
                                    <td className="border px-3 py-2">{booking.guests}</td>
                                    <td className="border px-3 py-2 whitespace-pre-wrap">
                                        {booking.notes || "-"}
                                    </td>
                                    <td className="border px-3 py-2">{booking.start_date}</td>
                                    <td className="border px-3 py-2">{booking.created_at}</td>
                                    <td className="border px-3 py-2">
                                        {booking.status ? (
                                            <span
                                                className={`px-2 py-1 rounded-full text-sm font-semibold
                                                ${booking.status === "confirmed" ? "bg-green-100 text-green-700" : ""}
                                                ${booking.status === "pending" ? "bg-yellow-100 text-yellow-700" : ""}
                                                ${booking.status === "canceled" ? "bg-red-100 text-red-700" : ""}
                                                ${!["confirmed", "pending", "canceled"].includes(booking.status) ? "bg-gray-100 text-gray-700" : ""}
                                            `}
                                            >
      {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
    </span>
                                        ) : (
                                            <span
                                                className="px-2 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">
      Pending
    </span>
                                        )}
                                    </td>

                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="text-center p-4">
                                    No bookings found.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    <div className="flex flex-wrap gap-2 mt-4">
                        {links.map((link, i) => (
                            <button
                                key={i}
                                onClick={() =>
                                    link.url &&
                                    fetchBookings(new URL(link.url).pathname + new URL(link.url).search)
                                }
                                disabled={!link.url}
                                className={`px-3 py-1 border rounded ${
                                    link.active ? "bg-blue-500 text-white" : "bg-white"
                                } ${!link.url ? "opacity-50 cursor-not-allowed" : ""}`}
                                dangerouslySetInnerHTML={{__html: link.label}}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
